import { ScopeDescription } from '@app/ccf/company-report/utils';
import ReportContent from '@app/components/reports/components/report-content';
import {
  Category,
  CompanyReportCategoryStatusStatusEnum,
  Scope,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import {
  capitalizeFirstLetter,
  cn,
  formatBigNumber,
  formatNumber,
  removeUnderScore,
  roundNumberToNDecimalPlaces,
} from '@shared/lib/utils';
import { useCCFReportResult } from '../../ccf-report-result-context';

export const scope3StatusesToDisplay: CompanyReportCategoryStatusStatusEnum[] =
  [
    CompanyReportCategoryStatusStatusEnum.included,
    CompanyReportCategoryStatusStatusEnum.no_emissions,
    CompanyReportCategoryStatusStatusEnum.data_unavailable,
  ];

const formatCategoryEmissions = (category: Category): string | number => {
  switch (category.status) {
    case CompanyReportCategoryStatusStatusEnum.included:
      return formatNumber(category.totalCategoryEmissionsTonnes!);
    case CompanyReportCategoryStatusStatusEnum.no_emissions:
      return roundNumberToNDecimalPlaces(
        category.totalCategoryEmissionsTonnes,
        2
      );

    case CompanyReportCategoryStatusStatusEnum.data_unavailable:
      return 'Data unavailable';

    case CompanyReportCategoryStatusStatusEnum.not_supported:
      return 'Not supported';

    default:
      return 'Not included';
  }
};

function YFCRow({
  scope,
  scopeName,
  notIncluded,
}: {
  scope: Scope;
  scopeName: string;
  notIncluded?: boolean;
}) {
  const categoriesToDisplay =
    scopeName !== 'scope 3'
      ? scope.categories
      : scope.categories.filter((category) =>
          scope3StatusesToDisplay.includes(
            category.status as CompanyReportCategoryStatusStatusEnum
          )
        );

  return (
    <div
      className={cn('grid grid-cols-4 h-full pb-4 items-stretch', {
        'border-b': scopeName !== 'scope 3',
      })}
    >
      <div>
        <Text
          className={cn('text-2xl font-medium text-primary-800', {
            'text-primary-400': notIncluded,
          })}
        >
          {capitalizeFirstLetter(removeUnderScore(scopeName))}
        </Text>
        <Text
          variant="reportBody"
          className="max-w-[180px] text-md text-primary-600"
        >
          {ScopeDescription[scopeName as 'scope 1' | 'scope 2' | 'scope 3']}
        </Text>
      </div>
      <div className="col-span-2">
        {notIncluded ? (
          <Text variant="reportBody" className="text-lg text-primary-400">
            Not included in this report
          </Text>
        ) : (
          categoriesToDisplay.map((category, idx) => {
            return (
              <div className="flex w-full justify-between" key={idx}>
                <Text
                  variant="reportBody"
                  className="text-lg  text-primary-600"
                >
                  {category.name}
                </Text>
                <Text
                  variant="reportBody"
                  className="text-lg  text-primary-600"
                >
                  {formatCategoryEmissions(category)}
                </Text>
              </div>
            );
          })
        )}
      </div>
      <div className="flex flex-col items-end">
        <span className="flex flex-1 items-baseline justify-self-end">
          {notIncluded ? (
            <Text className="mr-3 text-3xl font-medium text-primary-400">
              -
            </Text>
          ) : (
            <Text className="mr-1 text-3xl font-medium text-primary-800">
              {formatNumber(Number(scope.totalScopeEmissionsTonnes!))}
            </Text>
          )}
          <Unit variant="tCO2e" />
        </span>
      </div>
    </div>
  );
}

export default function YourFullCalculation() {
  const {
    result,
    result: { scope1, scope2, scope3, includesScope3 },
  } = useCCFReportResult();

  const hasExcludedScope3Categories =
    scope3.categories.filter(
      (category) =>
        !scope3StatusesToDisplay.includes(
          category.status as CompanyReportCategoryStatusStatusEnum
        )
    ).length > 0;

  const { downloadMode } = useCCFReportResult();
  return (
    <ReportContent variant="appendix">
      <div
        className={cn('flex flex-col flex-1', {
          // 'gap-y-8': !downloadMode,
        })}
      >
        <div className="grid grid-cols-4">
          <div></div>
          <div
            className={cn('col-span-2 mb-4', {
              'mb-6': downloadMode,
            })}
          >
            <div className="flex w-full justify-between">
              <Text variant="reportBody" className="font-medium">
                Category
              </Text>
              <span className="flex items-baseline">
                <Text variant="reportBody" className="font-medium">
                  Emissions
                </Text>
                <Unit variant="tCO2e" className="ml-1" />
              </span>
            </div>
          </div>
        </div>

        <div className="mb-8 flex flex-col space-y-4">
          <YFCRow scope={scope1} scopeName={'scope 1'} />
          <YFCRow scope={scope2} scopeName={'scope 2'} />
          <YFCRow
            scope={scope3}
            scopeName={'scope 3'}
            notIncluded={!includesScope3}
          />
        </div>

        <div className="grid grid-cols-4">
          <div className="col-span-3">
            {includesScope3 && hasExcludedScope3Categories && (
              <Text variant="subtle" className="text-sm font-light">
                All other Scope 3 categories are excluded from this report
                because they are not deemed significant for the reporting
                business.
              </Text>
            )}
          </div>
          <div className="col-span-1 place-items-end text-teal-500">
            <Text>Total Carbon Footprint</Text>
            <span className="flex items-baseline">
              <Text className="text-3xl font-medium text-teal-500">
                {formatBigNumber(Number(result.totalCompanyEmissionsTonnes))}
              </Text>
              <Unit variant="tCO2e" className="ml-1 text-teal-500" />
            </span>
          </div>
        </div>
      </div>
    </ReportContent>
  );
}
